import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Users,
  Loader2,
  Plus,
  Search,
  X,
  Mail,
  Linkedin,
  Phone,
  Building,
  Upload,
  FileText,
  CheckCircle2,
  Eye,
} from 'lucide-react';
import { useApi } from '../api/client';
import { useCatalogs } from '../api/useCatalogs';
import { useQuickLog } from '../components/QuickLogProvider';
import type { Contact } from '../types/api';
import ContactEditModal, {
  EMPTY_CONTACT_FORM,
  fromContact,
  type ContactFormState,
} from '../components/ContactEditModal';
import Pager from '../components/Pager';

interface ContactsResponse {
  contacts: Contact[];
  total: number;
  page: number;
  size: number;
}

const PAGE_SIZE = 50;

export default function Contacts() {
  const apiFetch = useApi();
  const { catalogs } = useCatalogs();
  const [data, setData] = useState<ContactsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState<number | 'all'>('all');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<ContactFormState | null>(null);
  const [importing, setImporting] = useState(false);

  // Debounce the search box so type-ahead hits the server ~250ms after the
  // last keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const refetch = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedQuery.trim()) params.set('q', debouncedQuery.trim());
      if (platformFilter !== 'all') params.set('platform_id', String(platformFilter));
      params.set('page', String(page));
      params.set('size', String(PAGE_SIZE));
      const r = await apiFetch(`/contacts?${params.toString()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: ContactsResponse = await r.json();
      setData(d);
    } catch (e) {
      setError(String(e));
    }
  }, [apiFetch, debouncedQuery, platformFilter, page]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Quick-log can create a contact; refresh so the new row shows.
  const { onLogged } = useQuickLog();
  useEffect(() => {
    return onLogged(() => {
      refetch();
    });
  }, [onLogged, refetch]);

  const platformLabel = (id: number | null): string => {
    if (id == null || !catalogs) return '—';
    return catalogs.platforms.find((p) => p.id === id)?.description ?? '—';
  };

  const contacts = data?.contacts ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex items-center gap-3">
        <Users className="h-6 w-6 text-brand-mint-600" />
        <h1 className="font-display text-3xl font-black text-brand-forest">
          Contacts
        </h1>
        {data && (
          <span className="text-sm text-brand-sage">
            {data.total} {data.total === 1 ? 'contact' : 'contacts'}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-sage" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name…"
            className="w-full rounded-md border border-brand-sage/40 bg-white py-2 pl-9 pr-3 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
          />
        </div>
        {catalogs && (
          <select
            value={platformFilter}
            onChange={(e) => {
              setPlatformFilter(
                e.target.value === 'all' ? 'all' : Number(e.target.value),
              );
              setPage(1);
            }}
            className="rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
          >
            <option value="all">All platforms</option>
            {catalogs.platforms.map((p) => (
              <option key={p.id} value={p.id}>
                {p.description}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => setImporting(true)}
          className="flex items-center gap-2 rounded-xl border border-brand-sage/40 bg-white px-4 py-2 font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-mint-100/40"
        >
          <Upload className="h-4 w-4" strokeWidth={3} />
          Import
        </button>
        <button
          type="button"
          onClick={() => setEditing({ ...EMPTY_CONTACT_FORM })}
          className="flex items-center gap-2 rounded-xl bg-brand-lime px-4 py-2 font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-lime-600"
        >
          <Plus className="h-4 w-4" strokeWidth={3} />
          New contact
        </button>
        {data && (
          <Pager
            page={data.page}
            size={data.size}
            total={data.total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {data == null ? (
        <div className="flex items-center gap-2 text-brand-sage">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading contacts…</span>
        </div>
      ) : contacts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-sage/40 bg-white p-10 text-center">
          <Users className="mx-auto mb-3 h-8 w-8 text-brand-sage" />
          <p className="text-brand-forest">
            {query || platformFilter !== 'all'
              ? 'No contacts match those filters.'
              : 'No contacts yet. Add one with the New contact button, or just quick-log an outreach with a name and a contact will be created automatically.'}
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto rounded-2xl border border-brand-sage/30 bg-white shadow-sm">
          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-brand-paper">
              <tr className="border-b border-brand-sage/20 bg-brand-paper text-left text-xs font-bold uppercase tracking-wider text-brand-sage">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Platform</th>
                <th className="px-4 py-3">Last touched</th>
                <th className="px-4 py-3">Reach</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setEditing(fromContact(c))}
                  className="cursor-pointer border-b border-brand-sage/10 last:border-0 hover:bg-brand-mint-100/40"
                >
                  <td className="px-4 py-3 font-bold text-brand-forest">
                    {c.name}
                    {c.company_or_org && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs text-brand-sage">
                        <Building className="h-3 w-3" />
                        {c.company_or_org}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-brand-forest">
                    {platformLabel(c.platform_id)}
                  </td>
                  <td className="px-4 py-3 text-brand-sage">
                    {c.last_touched_at
                      ? new Date(c.last_touched_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-brand-sage">
                      {c.email && <Mail className="h-3.5 w-3.5" />}
                      {c.linkedin_url && <Linkedin className="h-3.5 w-3.5" />}
                      {c.phone && <Phone className="h-3.5 w-3.5" />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ContactEditModal
        form={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refetch();
        }}
        onChanged={refetch}
      />

      <ImportModal
        open={importing}
        onClose={() => setImporting(false)}
        onImported={refetch}
      />
    </div>
  );
}

interface ImportChange {
  action: 'create' | 'update';
  name: string;
  email: string | null;
  phone: string | null;
}

interface ImportResult {
  imported: number;
  updated: number;
  skipped_duplicates: number;
  skipped_empty: number;
  total_rows: number;
  dry_run: boolean;
  changes: ImportChange[];
}

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

function ImportModal({ open, onClose, onImported }: ImportModalProps) {
  const apiFetch = useApi();
  const { catalogs } = useCatalogs();
  const inputRef = useRef<HTMLInputElement>(null);
  const [platformId, setPlatformId] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  // Reset everything whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setPlatformId('');
      setFile(null);
      setDragging(false);
      setDryRun(false);
      setBusy(false);
      setError(null);
      setResult(null);
    }
  }, [open]);

  if (!open) return null;

  function pickFile(f: File | null | undefined) {
    if (!f) return;
    setError(null);
    setResult(null);
    setFile(f);
  }

  async function runImport(dry: boolean) {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const csv = await file.text();
      const r = await apiFetch('/contacts/import', {
        method: 'POST',
        body: JSON.stringify({
          csv,
          platform_id: platformId === '' ? null : platformId,
          dry_run: dry,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: ImportResult = await r.json();
      setResult(d);
      // A dry run changes nothing, so there's nothing to refresh.
      if (!d.dry_run) onImported();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-brand-forest-900/60 px-4 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.92, y: 10, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-brand-mint to-brand-sky px-5 py-3 text-brand-forest">
            <div className="font-display font-black">Import contacts</div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 transition hover:bg-brand-forest/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4 px-5 py-5">
            {result ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-brand-forest">
                  {result.dry_run ? (
                    <Eye className="h-5 w-5 text-brand-violet" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-brand-mint-600" />
                  )}
                  <span className="font-display font-black">
                    {result.dry_run ? 'Import preview' : 'Import complete'}
                  </span>
                </div>
                <ul className="space-y-1 text-sm text-brand-forest">
                  <li>
                    <span className="font-bold">{result.imported}</span> contact
                    {result.imported === 1 ? '' : 's'}{' '}
                    {result.dry_run ? 'would be imported' : 'imported'}
                  </li>
                  {result.updated > 0 && (
                    <li>
                      <span className="font-bold">{result.updated}</span> existing
                      contact{result.updated === 1 ? '' : 's'}{' '}
                      {result.dry_run ? 'would be updated' : 'updated'} with email
                      / phone
                    </li>
                  )}
                  {result.skipped_duplicates > 0 && (
                    <li className="text-brand-sage">
                      {result.skipped_duplicates} duplicate
                      {result.skipped_duplicates === 1 ? '' : 's'} skipped
                    </li>
                  )}
                  {result.skipped_empty > 0 && (
                    <li className="text-brand-sage">
                      {result.skipped_empty} empty row
                      {result.skipped_empty === 1 ? '' : 's'} skipped
                    </li>
                  )}
                </ul>

                {result.dry_run && (
                  <>
                    {result.changes.length > 0 ? (
                      <ul className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-brand-sage/30 bg-brand-paper p-2 text-sm">
                        {result.changes.map((c, i) => (
                          <li
                            key={`${c.name}-${i}`}
                            className="flex items-start gap-2"
                          >
                            <span
                              className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-xs font-bold uppercase ${
                                c.action === 'create'
                                  ? 'bg-brand-mint/30 text-brand-forest'
                                  : 'bg-brand-sky/30 text-brand-forest'
                              }`}
                            >
                              {c.action === 'create' ? 'New' : 'Update'}
                            </span>
                            <span className="font-bold text-brand-forest">
                              {c.name}
                            </span>
                            <span className="text-brand-sage">
                              {[c.email, c.phone].filter(Boolean).join(' · ') ||
                                '—'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-brand-sage">
                        No changes — every row already matches an existing
                        contact.
                      </p>
                    )}
                    <p className="text-xs font-bold uppercase tracking-wider text-brand-violet">
                      Preview only — nothing was saved.
                    </p>
                  </>
                )}

                <div className="flex justify-end gap-2">
                  {result.dry_run ? (
                    <>
                      <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md px-4 py-2 text-sm text-brand-forest hover:bg-brand-sage/10"
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        onClick={() => runImport(false)}
                        disabled={busy || result.imported + result.updated === 0}
                        className="flex items-center gap-2 rounded-xl bg-brand-lime px-4 py-2 font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-lime-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4" />
                        )}
                        Apply import
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-xl bg-brand-lime px-4 py-2 font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-lime-600"
                    >
                      Done
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-brand-sage">
                  Upload a CSV file. Include a header row with{' '}
                  <span className="font-bold">Name</span>,{' '}
                  <span className="font-bold">Email</span>, and/or{' '}
                  <span className="font-bold">Phone</span> columns; without a
                  header, the first column is used as the full name. New contacts
                  start with the <span className="font-bold">New</span> status. If
                  a name already exists, a blank email or phone is filled in from
                  the file — existing values, names, and platforms are never
                  overwritten.
                </p>

                {catalogs && (
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-sage">
                      Platform for imported contacts
                    </label>
                    <select
                      value={platformId}
                      onChange={(e) =>
                        setPlatformId(
                          e.target.value === '' ? '' : Number(e.target.value),
                        )
                      }
                      className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                    >
                      <option value="">— None —</option>
                      {catalogs.platforms.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.description}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    pickFile(e.dataTransfer.files?.[0]);
                  }}
                  onClick={() => inputRef.current?.click()}
                  className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition ${
                    dragging
                      ? 'border-brand-lime bg-brand-lime/10'
                      : 'border-brand-sage/40 bg-brand-paper hover:bg-brand-mint-100/40'
                  }`}
                >
                  {file ? (
                    <>
                      <FileText className="h-7 w-7 text-brand-mint-600" />
                      <span className="font-bold text-brand-forest">
                        {file.name}
                      </span>
                      <span className="text-xs text-brand-sage">
                        Click or drop to choose a different file
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-7 w-7 text-brand-sage" />
                      <span className="font-bold text-brand-forest">
                        Drop a CSV here, or click to browse
                      </span>
                      <span className="text-xs text-brand-sage">.csv files</span>
                    </>
                  )}
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => {
                      pickFile(e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                </div>

                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <label className="flex cursor-pointer items-center gap-2 text-sm text-brand-forest">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    className="h-4 w-4 rounded border-brand-sage/50 text-brand-lime focus:ring-brand-lime/30"
                  />
                  <span>
                    Dry run — preview changes without saving
                  </span>
                </label>

                <div className="flex justify-end gap-2 border-t border-brand-sage/20 pt-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md px-4 py-2 text-sm text-brand-forest hover:bg-brand-sage/10"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => runImport(dryRun)}
                    disabled={!file || busy}
                    className="flex items-center gap-2 rounded-xl bg-brand-lime px-4 py-2 font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-lime-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : dryRun ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {busy
                      ? dryRun
                        ? 'Previewing…'
                        : 'Importing…'
                      : dryRun
                        ? 'Preview'
                        : 'Import'}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
